import * as cheerio from 'cheerio';
import dayjs from 'dayjs';
import _ from 'lodash';

const splitAndJoinStringByWhiteSpace = (str) => {
  if (typeof str !== 'string') return '';
  return str.split(/\s+/g).join(' ');
};

const parseDateString = (str) => {
  if (typeof str !== 'string') return '';
  return str.split(/[+-]/g)[0];
};

const getDocumentId = (ccd) => {
  const $ = cheerio.load(ccd, { xmlMode: true });
  const $ClinicalDocument = $('ClinicalDocument');

  const documentId = $ClinicalDocument.find('> id').attr('root');

  return documentId;
};

const getDocumentSetId = (ccd) => {
  const $ = cheerio.load(ccd, { xmlMode: true });
  const $ClinicalDocument = $('ClinicalDocument');

  const documentSetId = $ClinicalDocument.find('> setId').attr('root');

  return documentSetId;
};

const getDocumentVersionNumber = (ccd) => {
  const $ = cheerio.load(ccd, { xmlMode: true });
  const $ClinicalDocument = $('ClinicalDocument');

  const documentVersionNumber = $ClinicalDocument
    .find('> versionNumber')
    .attr('value');

  return documentVersionNumber;
};

const getDocumentTitle = (ccd) => {
  const $ = cheerio.load(ccd, { xmlMode: true });
  const $ClinicalDocument = $('ClinicalDocument');

  const title = $ClinicalDocument.find('> title').text();

  return title;
};

const getDocumentEffectiveTime = (ccd) => {
  const $ = cheerio.load(ccd, { xmlMode: true });
  const $ClinicalDocument = $('ClinicalDocument');

  const effectiveTime = $ClinicalDocument.find('> effectiveTime').attr('value');
  const effectiveTimeParsed = parseDateString(effectiveTime);
  const effectiveTimeFormatted =
    dayjs(effectiveTimeParsed).format('YYYY-MM-DD');

  return effectiveTimeFormatted;
};

const getDocumentConfidentiality = (ccd) => {
  const $ = cheerio.load(ccd, { xmlMode: true });
  const $ClinicalDocument = $('ClinicalDocument');

  const confidentiality = $ClinicalDocument
    .find('> confidentialityCode')
    .attr('displayName');

  return confidentiality;
};

const getDocumentActRelationshipDocuments = (ccd) => {
  const $ = cheerio.load(ccd, { xmlMode: true });
  const $ClinicalDocument = $('ClinicalDocument');

  const $relatedDocument = $ClinicalDocument.find('relatedDocument');

  if (!$relatedDocument.length) {
    return {};
  }

  let relatedDocument = {};
  let parentDocuments = [];

  const type = $relatedDocument.attr('typeCode');
  relatedDocument.type = type;

  $relatedDocument.find('parentDocument').each((i, el) => {
    const $parentDocument = $(el);
    const parentDocumentId = $parentDocument.find('> id').attr('root');
    const parentDocumentSetId = $parentDocument.find('> setId').attr('root');
    const parentDocumentVersionNumber = $parentDocument
      .find('> versionNumber')
      .attr('value');

    parentDocuments.push({
      id: parentDocumentId,
      setId: parentDocumentSetId,
      versionNumber: parentDocumentVersionNumber
    });
  });

  relatedDocument.parentDocuments = parentDocuments;

  return relatedDocument;
};

const getServiceEventTime = (ccd) => {
  const $ = cheerio.load(ccd, { xmlMode: true });
  const $ClinicalDocument = $('ClinicalDocument');

  const $serviceEvent = $ClinicalDocument.find(
    '> documentationOf > serviceEvent'
  );

  let type, low, high;

  type = $serviceEvent.attr('classCode');
  const lowValue = $serviceEvent.find('effectiveTime > low').attr('value');
  const highValue = $serviceEvent.find('effectiveTime > high').attr('value');

  if (lowValue) {
    const lowParsed = parseDateString(lowValue);
    const lowFormatted = dayjs(lowParsed).format('YYYY-MM-DD');
    low = lowFormatted;
  }
  if (highValue) {
    const highParsed = parseDateString(highValue);
    const highFormatted = dayjs(highParsed).format('YYYY-MM-DD');
    high = highFormatted;
  }

  return { type, low, high };
};

const getPerformers = (ccd) => {
  const $ = cheerio.load(ccd, { xmlMode: true });
  const $ClinicalDocument = $('ClinicalDocument');

  let performers = [];

  $ClinicalDocument
    .find('> documentationOf > serviceEvent > performer')
    .each((i, el) => {
      const $performer = $(el);

      // Functional Role
      const functionRole = $performer
        .find('> functionCode')
        .attr('displayName');

      // Performance time
      let time = {};
      const timeLow = $performer.find('> time > low').attr('value');
      const timeHigh = $performer.find('> time > high').attr('value');

      if (timeLow) {
        const timeLowParsed = parseDateString(timeLow);
        const timeLowFormatted = dayjs(timeLowParsed).format('YYYY-MM-DD');
        time.low = timeLowFormatted;
      }
      if (timeHigh) {
        const timeHighParsed = parseDateString(timeHigh);
        const timeHighFormatted = dayjs(timeHighParsed).format('YYYY-MM-DD');
        time.high = timeHighFormatted;
      }

      // Code - Specialty?
      const code = $performer.find('> assignedEntity > code').text().trim();

      // assignedPerson
      const assignedPerson = {
        name: splitAndJoinStringByWhiteSpace(
          $performer
            .find('> assignedEntity > assignedPerson > name')
            .first()
            .text()
            .trim()
        )
      };

      // Telecom
      let telecom = [];
      $performer.find('> assignedEntity > telecom').each((i, el) => {
        const value = $(el).attr('value');
        telecom.push(value);
      });

      // Address
      const $address = $performer.find('> assignedEntity > addr');
      const street = $address.find('streetAddressLine').text().trim();
      const city = $address.find('city').text().trim();
      const state = $address.find('state').text().trim();
      const postalCode = $address.find('postalCode').text().trim();

      const address = {
        street,
        city,
        state,
        postalCode
      };

      // Represented organization
      let representedOrganization = {};
      const orgTelecom = $performer
        .find('> assignedEntity > representedOrganization > telecom')
        .attr('value');
      const orgAddress = $performer
        .find('> assignedEntity > representedOrganization > addr')
        .attr('value');
      if (orgTelecom) {
        representedOrganization.telecom = orgTelecom;
      }
      if (orgAddress) {
        representedOrganization.address = orgAddress;
      }

      performers.push({
        functionRole,
        time,
        code,
        assignedPerson,
        address,
        telecom,
        representedOrganization
      });
    });

  return performers;
};

const getEncompassingEncounter = (ccd) => {
  const $ = cheerio.load(ccd, { xmlMode: true });
  const $ClinicalDocument = $('ClinicalDocument');

  const $encompassingEncounter = $ClinicalDocument.find(
    'componentOf > encompassingEncounter'
  );

  if (!$encompassingEncounter.length) return {};

  const type = $encompassingEncounter.find('> code > originalText').text();

  let time = {};
  const timeLow = $encompassingEncounter
    .find('> effectiveTime > low')
    .attr('value');
  const timeHigh = $encompassingEncounter
    .find('> effectiveTime > high')
    .attr('value');

  if (timeLow) {
    const timeLowParsed = parseDateString(timeLow);
    const timeLowFormatted = dayjs(timeLowParsed).format('YYYY-MM-DD');
    time.low = timeLowFormatted;
  }
  if (timeHigh) {
    const timeHighParsed = parseDateString(timeHigh);
    const timeHighFormatted = dayjs(timeHighParsed).format('YYYY-MM-DD');
    time.high = timeHighFormatted;
  }

  // Responsible Part
  const $responsibleParty = $encompassingEncounter.find('> responsibleParty');
  const $rpAddress = $responsibleParty.find('> assignedEntity > addr');

  const responsibleParty = {
    address: {
      street: $rpAddress.find('streetAddressLine').text().trim(),
      city: $rpAddress.find('city').text().trim(),
      state: $rpAddress.find('state').text().trim(),
      postalCode: $rpAddress.find('postalCode').text().trim()
    },
    telecom: [],
    assignedPerson: {
      name: splitAndJoinStringByWhiteSpace(
        $responsibleParty
          .find('> assignedEntity > assignedPerson > name')
          .first()
          .text()
          .trim()
      )
    },
    representedOrganization: $responsibleParty
      .find('> assignedEntity > representedOrganization > name')
      .text()
      .trim()
  };

  $responsibleParty.find('> assignedEntity > telecom').each((i, el) => {
    const value = $(el).attr('value');
    responsibleParty.telecom.push(value);
  });

  // Encounter Participant
  const $encounterParticipant = $encompassingEncounter.find(
    '> encounterParticipant'
  );

  const participant = {
    type: $encounterParticipant.attr('typeCode'),
    time: dayjs(
      parseDateString($encounterParticipant.find('> time').attr('value'))
    ).format('YYYY-MM-DD'),
    code: $encounterParticipant
      .find('> assignedEntity > code > originalText')
      .text()
      .trim(),
    address: {
      street: $encounterParticipant
        .find('> assignedEntity > addr > streetAddressLine')
        .text()
        .trim(),
      city: $encounterParticipant
        .find('> assignedEntity > addr > city')
        .text()
        .trim(),
      state: $encounterParticipant
        .find('> assignedEntity > addr > state')
        .text()
        .trim(),
      postalCode: $encounterParticipant
        .find('> assignedEntity > addr > postalCode')
        .text()
        .trim()
    },
    telecom: [],
    assignedPerson: {
      name: splitAndJoinStringByWhiteSpace(
        $encounterParticipant
          .find('> assignedEntity > assignedPerson > name > given')
          .first()
          .text()
          .trim()
      )
    }
  };

  const $healthCareFacility = $encompassingEncounter.find(
    '> location > healthCareFacility'
  );

  const healthCareFacility = {
    location: {
      street: $healthCareFacility
        .find('> location > addr > streetAddressLine')
        .text()
        .trim(),
      city: $healthCareFacility.find('> location > addr > city').text().trim(),
      state: $healthCareFacility
        .find('> location > addr > state')
        .text()
        .trim(),
      postalCode: $healthCareFacility
        .find('> location > addr > postalCode')
        .text()
        .trim()
    },
    serviceProviderOrganization: {
      name: $healthCareFacility
        .find('> serviceProviderOrganization > name')
        .text()
        .trim(),
      address: {
        street: $healthCareFacility
          .find('> serviceProviderOrganization > addr > streetAddressLine')
          .text()
          .trim(),
        city: $healthCareFacility
          .find('> serviceProviderOrganization > addr > city')
          .text()
          .trim(),
        state: $healthCareFacility
          .find('> serviceProviderOrganization > addr > state')
          .text()
          .trim(),
        postalCode: $healthCareFacility
          .find('> serviceProviderOrganization > addr > postalCode')
          .text()
          .trim()
      },
      asPartOfOrganization: $healthCareFacility
        .find(
          '> serviceProviderOrganization > asOrganizationPartOf > wholeOrganization > name'
        )
        .text()
        .trim()
    }
  };

  return {
    type,
    time,
    responsibleParty,
    participant,
    healthCareFacility
  };
};

const getPatient = (ccd) => {
  const $ = cheerio.load(ccd, { xmlMode: true });
  const $ClinicalDocument = $('ClinicalDocument');

  const $patientRole = $ClinicalDocument.find('> recordTarget > patientRole');

  let gender = $('patient > administrativeGenderCode').attr('code');
  if (gender) {
    gender = gender === 'M' ? 'Male' : gender === 'F' ? 'Female' : null;
  }

  const patient = {
    name: splitAndJoinStringByWhiteSpace(
      $patientRole.find('> patient > name').first().text().trim()
    ),
    address: {
      street: $patientRole.find('> addr > streetAddressLine').text().trim(),
      city: $patientRole.find('> addr > city').text().trim(),
      state: $patientRole.find('> addr > state').text().trim(),
      postalCode: $patientRole.find('> addr > postalCode').text().trim()
    },
    telecom: [],
    gender: gender,
    dob: dayjs(
      parseDateString($patientRole.find('> patient > birthTime').attr('value'))
    ).format('YYYY-MM-DD'),
    maritalStatus: $patientRole
      .find('> patient > maritalStatusCode')
      .attr('displayName'),
    ethnicity: $patientRole
      .find('> patient > ethnicGroupCode')
      .attr('displayName')
  };

  $patientRole.find('> telecom').each((i, el) => {
    const value = $(el).attr('value');
    patient.telecom.push(value);
  });

  return patient;
};

const getProviderOrganization = (ccd) => {
  const $ = cheerio.load(ccd, { xmlMode: true });
  const $ClinicalDocument = $('ClinicalDocument');

  const $providerOrg = $ClinicalDocument.find(
    '> recordTarget > patientRole > providerOrganization'
  );

  const providerOrganization = {
    name: $providerOrg.find('> name').text().trim(),
    address: {
      street: $providerOrg.find('> addr > streetAddressLine').text().trim(),
      city: $providerOrg.find('> addr > city').text().trim(),
      state: $providerOrg.find('> addr > state').text().trim(),
      postalCode: $providerOrg.find('> addr > postalCode').text().trim()
    }
  };

  return providerOrganization;
};

const getAuthor = (ccd) => {
  const $ = cheerio.load(ccd, { xmlMode: true });
  const $ClinicalDocument = $('ClinicalDocument');

  const $author = $ClinicalDocument.find('> author');

  const author = {
    time: dayjs(parseDateString($author.find('> time').attr('value'))).format(
      'YYYY-MM-DD'
    ),
    device: {
      model: $author
        .find(
          '> assignedAuthor > assignedAuthoringDevice > manufacturerModelName'
        )
        .text()
        .trim(),
      software: $author
        .find('> assignedAuthor > assignedAuthoringDevice > softwareName')
        .text()
        .trim()
    },
    representedOrganization: {
      name: $author
        .find('> assignedAuthor > representedOrganization > name')
        .text()
        .trim(),
      address: {
        street: $author
          .find(
            '> assignedAuthor > representedOrganization > addr > streetAddressLine'
          )
          .text()
          .trim(),
        city: $author
          .find('> assignedAuthor > representedOrganization > addr > city')
          .text()
          .trim(),
        state: $author
          .find('> assignedAuthor > representedOrganization > addr > state')
          .text()
          .trim(),
        postalCode: $author
          .find(
            '> assignedAuthor > representedOrganization > addr > postalCode'
          )
          .text()
          .trim()
      }
    }
  };

  return author;
};

const getDocumentType = (ccd) => {
  const $ = cheerio.load(ccd, { xmlMode: true });
  const $ClinicalDocument = $('ClinicalDocument');

  const type = {
    code: $ClinicalDocument.find('> code').attr('code'),
    codeSystem: $ClinicalDocument.find('> code').attr('codeSystemName'),
    name: $ClinicalDocument.find('> code').attr('displayName')
  };

  return type;
};

export const extractMetaDataFromCDA = (xmlStr) => {
  try {
    if (!xmlStr || typeof xmlStr !== 'string') {
      throw new Error('[parseCDAMetaData] argument must be a valid CDA string');
    }

    // const confidentiality = getDocumentConfidentiality(xmlStr);
    // const relatedDocument = getDocumentActRelationshipDocuments(xmlStr);
    // const id = getDocumentId(xmlStr);
    // const setId = getDocumentSetId(xmlStr);
    // const versionNumber = getDocumentVersionNumber(xmlStr);
    // const providerOrganization = getProviderOrganization(xmlStr);
    const encompassingEncounter = getEncompassingEncounter(xmlStr);
    const type = getDocumentType(xmlStr);
    const title = getDocumentTitle(xmlStr);
    const effectiveTime = getDocumentEffectiveTime(xmlStr);
    const serviceEventTime = getServiceEventTime(xmlStr);
    const performers = getPerformers(xmlStr);
    const patient = getPatient(xmlStr);
    const author = getAuthor(xmlStr);

    let performerAdresses = [];
    performers.forEach((p) => {
      const addressValues = Object.values(p.address);
      if (addressValues.length && addressValues.join('') !== '')
        performerAdresses.push(addressValues.join(', '));
    });

    let performerAddress = performerAdresses.length
      ? performerAdresses.join(', ')
      : '';

    let serviceEventTimeFormatted = '';
    if (serviceEventTime.low && serviceEventTime.high && serviceEventTime.low !== serviceEventTime.high) {
      serviceEventTimeFormatted = `From ${serviceEventTime.low} to ${serviceEventTime.high}`;
    } else {
      serviceEventTimeFormatted = serviceEventTime.low || serviceEventTime.high || '';
    }

    return {
      'Patient Name': patient.name.replace(/([A-Z])/g, ' $1').trim(),
      'Patient DOB': patient.dob,
      'Patient Gender': patient.gender,
      'Patient Address': Object.values(patient.address).join(', '),
      'Patient Contact': patient.telecom.join(', '),
      'Document Title': title,
      'Document Type': type.name,
      'Document Type LOINC': type.code,
      'Document Created at': `${dayjs(effectiveTime).format(
        'MM-DD-YYYY hh:mmA'
      )}`,
      'Service Event Date': serviceEventTimeFormatted,
      'Encounter Type': encompassingEncounter.type,
      'Author Device': Object.values(author.device).join(', '),
      Performer: performers.length
        ? performers
            .map((p) =>
              p.assignedPerson.name
                ? `${p.assignedPerson.name} ${
                    p.functionRole ? `(${p.functionRole})` : ''
                  }`
                : ''
            )
            .join(', ')
        : '',
      'Performer Address': performerAddress
    };
  } catch (error) {
    console.log('[parseCDAMetaData] error: ', error);
  }
};
